import { Resolver } from "react-hook-form";
import { parse, isBefore, isAfter, formatDate } from "date-fns";
import { z } from "zod";
import {
  CollectNodeFieldTypeEnum,
  CollectNodeFieldSchema,
} from "@aichat/flow-config";
import { isValueNumber } from "@aichat/aichat-lib";

export const cleanPhoneNumber = (phoneNumber: number | string) =>
  phoneNumber
    .toString()
    .replace(/^[^\d+]+/, "")
    .replace(/[()]/g, "");

export function dynamicFormResolver(
  fields: CollectNodeFieldSchema[],
): Resolver<Record<string, string | string[] | Date | boolean>> {
  return (values) => {
    const errors: Record<string, any> = {};
    for (const field of fields) {
      const { slot_name, type, mandatoryField } = field;
      const isRequired = mandatoryField === true;

      if (!slot_name) continue;

      const value = values[slot_name];

      // Common: required check
      if (value === undefined) {
        errors[slot_name] = { message: `Invalid value.` };
        continue;
      }

      switch (type) {
        case CollectNodeFieldTypeEnum.TEXT: {
          if (
            isRequired &&
            (!value || typeof value !== "string" || value.trim().length === 0)
          ) {
            errors[slot_name] = { message: "Text is required" };
          } else if (
            value &&
            (typeof value !== "string" || value.trim().length === 0)
          ) {
            errors[slot_name] = { message: "Please enter valid text." };
          }
          break;
        }

        case CollectNodeFieldTypeEnum.NUMBER: {
          if (Array.isArray(value)) {
            errors[slot_name] = { message: "Invalid value" };
            break;
          }

          if (isRequired && !value) {
            errors[slot_name] = { message: "Number is required" };
            break;
          }

          if (value) {
            const isNumber = isValueNumber(value.toString());

            if (!isNumber) {
              errors[slot_name] = { message: "Please enter valid number." };
              break;
            }

            const numValue = Number(value);
            const { minValue, maxValue, allowDecimal } = field;

            if (minValue !== undefined && numValue < Number(minValue)) {
              errors[slot_name] = { message: `Must be more than ${minValue}` };
            } else if (maxValue !== undefined && numValue > Number(maxValue)) {
              errors[slot_name] = { message: `Must be less than ${maxValue}` };
            } else if (value.toString().includes(".") && !allowDecimal) {
              errors[slot_name] = { message: "Cannot have decimal value" };
            }
          }
          break;
        }

        case CollectNodeFieldTypeEnum.EMAIL: {
          if (isRequired && !value) {
            errors[slot_name] = { message: "Email is required" };
            break;
          }

          if (value) {
            const { success } = z.string().email().safeParse(value);
            if (!success) {
              errors[slot_name] = { message: "Invalid Email" };
            }
          }
          break;
        }
        case CollectNodeFieldTypeEnum.PHONE: {
          if (Array.isArray(value)) {
            errors[slot_name] = { message: "Invalid value" };
            break;
          }

          const cleanedNumber = cleanPhoneNumber(value as string);

          if (isRequired && !cleanedNumber) {
            errors[slot_name] = { message: "Phone number is required" };
            break;
          }

          if (cleanedNumber) {
            if (cleanedNumber.toString()[0] !== "+") {
              errors[slot_name] = {
                message: `Phone number must start with a country code.`,
              };
            }
            const onlyPhoneNo = cleanedNumber.slice(1);

            if (!onlyPhoneNo || !isValueNumber(onlyPhoneNo)) {
              errors[slot_name] = { message: "Invalid phone number" };
              break;
            }

            if (field.countryCodes) {
              const isValid = field.countryCodes.some((code) => {
                return value.toString().startsWith(code);
              });

              if (!isValid) {
                errors[slot_name] = { message: "Country code not supported" };
              }
            }
          }

          break;
        }

        case CollectNodeFieldTypeEnum.SELECT: {
          const { singleSelect, selectValues } = field;

          if (singleSelect) {
            if (isRequired && !value) {
              errors[slot_name] = { message: "Please select a value" };
              break;
            }

            if (value) {
              if (Array.isArray(value)) {
                errors[slot_name] = { message: "Please select a valid value" };
                break;
              }
              const isValid = selectValues.some((v) => v.value === value);
              if (!isValid) {
                errors[slot_name] = { message: "Please select a valid value" };
              }
            }
          } else {
            if (
              isRequired &&
              (!value || (Array.isArray(value) && value.length === 0))
            ) {
              errors[slot_name] = {
                message: "Please select at least one value",
              };
              break;
            }

            if (!Array.isArray(value)) {
              errors[slot_name] = { message: "Please select valid values" };
              break;
            }

            if (value.length > 0) {
              const allValid = (value as string[]).every((val) =>
                selectValues.some((v) => v.value === val),
              );
              if (!allValid) {
                errors[slot_name] = { message: "Please select valid values" };
              }
            }
          }

          break;
        }

        case CollectNodeFieldTypeEnum.TIME: {
          if (Array.isArray(value)) {
            errors[slot_name] = { message: "Invalid value" };
            break;
          }

          if (isRequired && !value) {
            errors[slot_name] = { message: "Time is required" };
            break;
          }

          if (value) {
            const { acceptedTimes } = field;
            if (acceptedTimes && acceptedTimes.length > 0) {
              const parseTime = (str: string) =>
                parse(str, "hh:mm a", new Date(2000, 0, 1));
              const inputTime = parseTime(value.toString());
              const isInvalid = acceptedTimes.every(({ from, to }) => {
                const fromTime = parseTime(from);
                const toTime = parseTime(to);
                const crossesMidnight = isAfter(fromTime, toTime);
                return crossesMidnight
                  ? isBefore(inputTime, fromTime) && isAfter(inputTime, toTime)
                  : isBefore(inputTime, fromTime) || isAfter(inputTime, toTime);
              });

              if (isInvalid) {
                errors[slot_name] = { message: "Invalid time range" };
              }
            }
          }

          break;
        }

        case CollectNodeFieldTypeEnum.DATE: {
          const { dateRange } = field;

          if (!value && isRequired) {
            errors[slot_name] = { message: "Please enter valid date" };
            break;
          }

          if (typeof value === "boolean") {
            errors[slot_name] = { message: "Please enter valid date" };
            break;
          }

          const dateValue = new Date(value as string);

          if (isNaN(dateValue.getTime())) {
            errors[slot_name] = { message: "Invalid date format" };
            break;
          }

          if (dateRange) {
            const { startDate, endDate } = dateRange;

            if (startDate) {
              const start = new Date(startDate);
              if (isBefore(dateValue, start)) {
                errors[slot_name] = {
                  message: `Date should be on or after ${formatDate(start, "do MMM yyyy")}`,
                };
              }
            }

            if (endDate) {
              const end = new Date(endDate);
              if (isAfter(dateValue, end)) {
                errors[slot_name] = {
                  message: `Date should be on or before ${formatDate(end, "do MMM yyyy")}`,
                };
              }
            }
          }
          break;
        }

        case CollectNodeFieldTypeEnum.CONFIRMATION: {
          if (isRequired && !value)
            errors[slot_name] = {
              message: "You must check this box",
            };
        }
      }
    }

    return {
      values: Object.keys(errors).length === 0 ? values : {},
      errors,
    };
  };
}
